import React, { useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

const PROVISION_MAX_LENGTH = 5000;
const BULLET_MARKER = '• ';

export default function EditableProvisionCard(props) {
  const {
    data,
    index,
    lastIndex,
    isSelected,
    onSelect,
    onMoveUp,
    onMoveDown,
    onRemove,
    onChangeTitle,
    onChangeText
  } = props;

  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  // 본문 입력창 높이를 내용에 맞춰 자동 조절 (min-height는 CSS가 보장) //
  const bodyTextAreaRef = useRef(null);
  const pendingCaretRef = useRef(null);

  useLayoutEffect(() => {
    const el = bodyTextAreaRef.current;
    if (!el) return;

    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [data.text, isSelected]);

  // 리스트 자동 입력 후 커서 위치 복원 (controlled textarea) //
  useLayoutEffect(() => {
    if (pendingCaretRef.current === null) return;

    const el = bodyTextAreaRef.current;
    if (!el) return;

    el.selectionStart = el.selectionEnd = pendingCaretRef.current;
    pendingCaretRef.current = null;
  }, [data.text]);

  const translateTitle = (title) => {
    const titlesObj = t('myStoreSetting.terms.provisionTitles');
    if (typeof titlesObj === 'object' && titlesObj[title]) return titlesObj[title];
    return title;
  };

  const isFirst = index === 0;
  const isLast = index === lastIndex;

  function onClickCard() {
    if (!isSelected && onSelect) onSelect();
  }

  // 규정 본문 입력 - 5,000자 초과 시 차단 //
  function onChangeBodyText(e) {
    if (e.target.value.length > PROVISION_MAX_LENGTH) {
      alert(t('myStoreSetting.terms.provisionMaxLengthAlert'));
      return;
    }

    onChangeText(data.id, e.target.value);
  }

  // 리스트 자동 입력 - Enter로 머리표 이어쓰기 / '- ','* '로 불릿 변환 (노션 방식) //
  function onKeyDownBodyText(e) {
    // 한글 등 IME 조합 중에는 동작하지 않음 //
    if (e.nativeEvent.isComposing) return;

    const el = e.target;
    const caret = el.selectionStart;

    // 영역 선택 상태에서는 기본 동작 유지 //
    if (caret !== el.selectionEnd) return;

    const value = el.value;
    const lineStart = value.lastIndexOf('\n', caret - 1) + 1;
    const currentLine = value.slice(lineStart, caret);

    // '- ' 또는 '* '를 줄 시작에서 입력하면 불릿으로 변환 //
    if (e.key === ' ' && (currentLine === '-' || currentLine === '*')) {
      e.preventDefault();

      const newValue = value.slice(0, lineStart) + BULLET_MARKER + value.slice(caret);

      pendingCaretRef.current = lineStart + BULLET_MARKER.length;
      onChangeText(data.id, newValue);
      return;
    }

    if (e.key !== 'Enter' || e.shiftKey) return;

    const bulletMatch = currentLine.match(/^(• )(.*)$/);
    const numberMatch = currentLine.match(/^(\d+)\. (.*)$/);

    if (!bulletMatch && !numberMatch) return;

    const itemContent = bulletMatch ? bulletMatch[2] : numberMatch[2];

    // 내용 없는 머리표에서 Enter → 리스트 종료 (머리표 제거) //
    if (itemContent === '') {
      e.preventDefault();

      const newValue = value.slice(0, lineStart) + value.slice(caret);

      pendingCaretRef.current = lineStart;
      onChangeText(data.id, newValue);
      return;
    }

    // 내용 있는 머리표에서 Enter → 다음 줄에 머리표 자동 삽입 //
    e.preventDefault();

    const nextMarker = bulletMatch ? BULLET_MARKER : (parseInt(numberMatch[1], 10) + 1) + '. ';
    const insertText = '\n' + nextMarker;
    const newValue = value.slice(0, caret) + insertText + value.slice(caret);

    pendingCaretRef.current = caret + insertText.length;
    onChangeText(data.id, newValue);
  }

  function onClickMoveUp(e) {
    e.stopPropagation();
    if (!isFirst) onMoveUp(index);
  }

  function onClickMoveDown(e) {
    e.stopPropagation();
    if (!isLast) onMoveDown(index);
  }

  function onClickRemove(e) {
    e.stopPropagation();
    onRemove(index);
  }

  return (
    <CardWrapper isSelected={isSelected} onClick={onClickCard}>
      <CardHeader>
        {
          isSelected ?
            <TitleInputEl
              value={data.title}
              onChange={(e) => onChangeTitle(data.id, e.target.value)}
              onClick={(e) => e.stopPropagation()} />
            :
            <TitleTag>
              <TitleText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{translateTitle(data.title)}</TitleText>
            </TitleTag>
        }
        {
          isSelected &&
          <ActionGroup>
            <ArrowButton disabled={isFirst} onClick={onClickMoveUp}>
              <ArrowGlyph direction='up' />
            </ArrowButton>
            <ArrowButton disabled={isLast} onClick={onClickMoveDown}>
              <ArrowGlyph direction='down' />
            </ArrowButton>
            <RemoveButton onClick={onClickRemove}>
              <CrossStick rotate={45} />
              <CrossStick rotate={-45} />
            </RemoveButton>
          </ActionGroup>
        }
      </CardHeader>
      <CardBody>
        {
          isSelected ?
            <BodyTextAreaWrapper>
              <BodyTextArea
                ref={bodyTextAreaRef}
                value={data.text}
                onChange={onChangeBodyText}
                onKeyDown={onKeyDownBodyText}
                onClick={(e) => e.stopPropagation()} />
              {
                !data.text &&
                <BodyPlaceholder fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.line}>{t('myStoreSetting.terms.newProvisionPlaceholder')}</BodyPlaceholder>
              }
            </BodyTextAreaWrapper>
            :
            <BodyText fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{data.text}</BodyText>
        }
      </CardBody>
    </CardWrapper>
  );
}

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;

  border: 1px solid ${props => props.isSelected ? props.theme.primary : `transparent`};
  border-radius: 5px;
  background-color: ${props => props.isSelected ? props.theme.subBackground : `transparent`};

  box-sizing: border-box;

  transition: border-color 0.2s ease-in-out;

  @media (hover: hover) {
    &:hover {
      border-color: ${props => props.theme.primary};
      cursor: ${props => props.isSelected ? `default` : `pointer`};
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 10px 12px 10px 10px;

  width: calc(100% - 22px);
`;

const TitleTag = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 2px 6px;
`;

const TitleText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};

  white-space: nowrap;
`;

const TitleInputEl = styled.input`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;
  padding: 2px 6px;

  min-width: 120px;
  max-width: 70%;

  border: 1px dashed ${props => props.theme.line};
  border-radius: 2px;
  background-color: transparent;
  color: ${props => props.theme.text};

  outline: none;

  ::placeholder {
    color: ${props => props.theme.subText};
  }
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 15px;
`;

const ArrowButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 14px;
  height: 14px;

  color: ${props => props.theme.line};

  visibility: ${props => props.disabled ? `hidden` : `visible`};
  pointer-events: ${props => props.disabled ? `none` : `auto`};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      color: ${props => props.theme.primary};
    }
  }
`;

const ArrowGlyph = styled.div`
  width: 8px;
  height: 8px;

  border-top: 2px solid currentColor;
  border-right: 2px solid currentColor;

  transform: ${props => props.direction === 'up' ? `rotate(-45deg) translate(-2px, 2px)` : `rotate(135deg) translate(-2px, 2px)`};

  transition: all 0.2s ease-in-out;
`;

const RemoveButton = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 14px;
  height: 14px;

  color: ${props => props.theme.line};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      color: ${props => props.theme.pointRed};
    }
  }
`;

const CrossStick = styled.div`
  position: absolute;

  width: 14px;
  height: 2px;

  border-radius: 1px;
  background-color: currentColor;

  transform: rotate(${props => props.rotate}deg);

  transition: all 0.2s ease-in-out;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  padding: 0 10px 10px 10px;

  width: calc(100% - 20px);
`;

const BodyText = styled.pre`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  white-space: pre-wrap;

  color: ${props => props.color};
`;

const BodyTextAreaWrapper = styled.div`
  position: relative;
  display: flex;

  width: 100%;
`;

const BodyPlaceholder = styled.p`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  margin: 0;

  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  pointer-events: none;
  white-space: nowrap;
`;

const BodyTextArea = styled.textarea`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0;
  padding: 6px 8px;

  width: 100%;
  min-height: 160px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 5px;
  background-color: transparent;
  color: ${props => props.theme.text};

  outline: none;
  resize: none;
  overflow-y: hidden;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.primary};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  ::placeholder {
    color: ${props => props.theme.subText};
  }
`;
